<!-- ValueSetCard.vue -->
<template>
  <div class="card mb-4">
    <header class="card-header cursor-pointer" @click="toggleExpanded">
      <p class="card-header-title">
        {{ valueSet.name }} (updated {{ formatDate(valueSet.updated_at) }})
      </p>
      <span class="card-header-icon">
        <span class="icon">
          <i :class="['fas', isExpanded ? 'fa-angle-up' : 'fa-angle-down']"></i>
        </span>
      </span>
    </header>

    <div class="card-content" v-show="isExpanded">
      <div class="content">
        <div class="mb-4">
          <span class="has-text-grey">Owner: {{ valueSet.advisor_name }}</span>
        </div>
        <div class="mb-4">
          <span class="has-text-grey">Created on {{ formatDate(valueSet.created_at) }}</span>
          <span class="has-text-grey">Updated on {{ formatDate(valueSet.updated_at) }}</span>
        </div>

        <!-- Permissions section -->
        <div class="mb-4" v-if="isEditable">
          <h4 class="title is-6">Permissions</h4>
          <div v-if="isLoadingPermissions">
            Loading permissions...
          </div>
          <div v-else-if="permissions">
            <div v-if="permissions.structural" class="box">
              <p>Automatic Permissions</p>
              <ul>
                <li>Firm Admin has access: <strong>{{ permissions.structural.firm_admin_has_access ? 'Yes' : 'No' }}</strong></li>
                <li>Firm Members have access: <strong>{{ permissions.structural.firm_members_have_access ? 'Yes' : 'No' }}</strong></li>
                <li>Value Set is a template: <strong>{{ permissions.structural.is_template ? 'Yes' : 'No' }}</strong></li>
              </ul>
            </div>
            <div v-if="permissions.explicit && canManagePermissions" class="box">
              <p>Explicit Permissions</p>
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Can View</th>
                    <th>Can Edit</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in permissions.explicit" :key="user.id">
                    <td>{{ user.email }} <strong v-if="isOwner(user.id)">(owner)</strong></td>
                    <td>
                      <input type="checkbox"
                             :disabled="isOwner(user.id) || isUpdatingPermissions || !canManagePermissions"
                             :checked="hasPermission(user, 'view_metricscreenset')"
                             @change="updatePermission(user.email, 'view_metricscreenset', !hasPermission(user, 'view_metricscreenset'))">
                    </td>
                    <td>
                      <input type="checkbox"
                             :disabled="isOwner(user.id) || isUpdatingPermissions || !canManagePermissions"
                             :checked="hasPermission(user, 'change_metricscreenset')"
                             @change="updatePermission(user.email, 'change_metricscreenset', !hasPermission(user, 'change_metricscreenset'))">
                    </td>
                  </tr>
                  <!-- Add new user row -->
                  <tr v-if="canManagePermissions">
                    <td>
                      <div class="select is-fullwidth">
                        <select v-model="newUserEmail" @focus="loadUserOptions">
                          <option value="">Select user...</option>
                          <option v-for="email in filteredUsers" :key="email" :value="email">
                            {{ email }}
                          </option>
                        </select>
                      </div>
                    </td>
                    <td>
                      <input type="checkbox" v-model="newUserCanView">
                    </td>
                    <td>
                      <input type="checkbox" v-model="newUserCanEdit">
                    </td>
                    <td>
                      <button class="button is-small is-primary"
                              @click="addPermission"
                              :disabled="!newUserEmail">
                        Add
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Metrics section -->
        <div class="mb-4">
          <h4 class="title is-6">Metrics ({{ valueSet.metrics.length }})</h4>
          <div class="tags">
            <span
              v-for="metric in valueSet.metrics"
              :key="metric.id"
              :class="['tag', 'is-light', 'is-' + getMetricCategory(metric)]"
            >
              {{ metric.details && metric.details.name }}
            </span>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="buttons">
          <button class="button is-info" v-if="isEditable" @click="$emit('edit', valueSet)">
            <span class="icon">
              <i class="fas fa-edit"></i>
            </span>
            <span>Edit Values Set</span>
          </button>
          <button class="button is-danger is-light" v-if="isEditable" @click="confirmDelete">
            <span class="icon">
              <i class="fas fa-trash"></i>
            </span>
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ValueSetCard',
  props: {
    valueSet: {
      type: Object,
      required: true
    },
    editableSets: {
      type: Set,
      default: () => new Set()
    },
  },
  data() {
    return {
      isExpanded: false,
      permissions: null,
      isLoadingPermissions: false,
      isUpdatingPermissions: false,
      newUserEmail: '',
      newUserCanView: true,
      newUserCanEdit: false,
      userOptions: [],
      filteredUsers: [],
      currentUserEmail: null,
    }
  },
  computed: {
    isEditable() {
      return this.editableSets && this.editableSets instanceof Set && this.editableSets.has(this.valueSet.id);
    },
    canManagePermissions() {
      if (!this.permissions || !this.permissions.structural || !this.currentUserEmail) return false;
      var structural = this.permissions.structural;
      var isOwner = structural.owner ? this.currentUserEmail === structural.owner.email : false;
      return isOwner || structural.is_current_user_firm_admin;
    },
  },
  methods: {
    isOwner(id) {
      return this.valueSet.user === id;
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString() : '';
    },
    getMetricCategory(metric) {
      return metric && metric.details && metric.details.subcategory && metric.details.subcategory[0] && metric.details.subcategory[0].esgissue
        ? metric.details.subcategory[0].esgissue.slug
        : 'default';
    },
    hasPermission(user, permission) {
      return user.permissions.includes(permission);
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded && this.isEditable && !this.permissions) {
        this.loadPermissions();
      }
    },
    async loadPermissions() {
      this.isLoadingPermissions = true;
      try {
        const requests = [fetch(`/api/v1/screen_sets/${this.valueSet.id}/permissions/`)];
        if (!this.currentUserEmail) requests.push(fetch('/api/v1/users/share_permissions/'));
        const [permResponse, userResponse] = await Promise.all(requests);
        if (!permResponse.ok) throw new Error('Failed to fetch permissions');
        this.permissions = await permResponse.json();
        if (userResponse) {
          const data = await userResponse.json();
          this.currentUserEmail = data.request_user;
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
        this.$buefy.toast.open({
          message: 'Failed to load permissions',
          type: 'is-danger'
        });
      } finally {
        this.isLoadingPermissions = false;
      }
    },
    async loadUserOptions() {
      try {
        const response = await fetch('/api/v1/users/share_permissions/');
        const data = await response.json();
        this.currentUserEmail = data.request_user;
        this.filteredUsers = data.permission_users.filter(function(user) { return user !== data.request_user; });
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    },
    applyPermissionChange(userEmail, permission, value) {
      const userIndex = this.permissions.explicit.findIndex(function(u) { return u.email === userEmail; });
      if (userIndex === -1) return false;

      const user = this.permissions.explicit[userIndex];
      const permissions = value
        ? user.permissions.concat([permission]).filter(function(p, i, arr) { return arr.indexOf(p) === i; })
        : user.permissions.filter(function(p) { return p !== permission; });

      if (permissions.length === 0) {
        this.permissions.explicit.splice(userIndex, 1);
      } else {
        this.$set(this.permissions.explicit, userIndex, Object.assign({}, user, { permissions: permissions }));
      }
      return true;
    },
    async updatePermission(userEmail, permission, value) {
      this.isUpdatingPermissions = true;
      try {
        const action = value ? 'add' : 'remove';
        const response = await fetch(`/api/v1/screen_sets/${this.valueSet.id}/permissions/${action}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            email: userEmail,
            permission: permission,
          })
        });
        if (!response.ok) throw new Error('Failed to update permission');
        const updatedExistingRow = this.applyPermissionChange(userEmail, permission, value);
        if (!updatedExistingRow && value) {
          await this.loadPermissions();
        }
        this.$buefy.toast.open({
          message: 'Permissions updated successfully',
          type: 'is-success'
        });
      } catch (error) {
        console.error('Error updating permission:', error);
        this.$buefy.toast.open({
          message: 'Failed to update permission',
          type: 'is-danger'
        });
        await this.loadPermissions();
      } finally {
        this.isUpdatingPermissions = false;
      }
    },
    async addPermission() {
      if (!this.newUserEmail) return;

      const permissions = [];
      if (this.newUserCanView) permissions.push('view_metricscreenset');
      if (this.newUserCanEdit) permissions.push('change_metricscreenset');

      try {
        for (var i = 0; i < permissions.length; i++) {
          await this.updatePermission(this.newUserEmail, permissions[i], true);
        }
        this.newUserEmail = '';
        this.newUserCanView = true;
        this.newUserCanEdit = false;
      } catch (error) {
        console.error('Failed to add permission:', error);
      }
    },
    confirmDelete() {
      this.$buefy.dialog.confirm({
        title: 'Delete Values Set',
        message: 'Are you sure you want to delete this values set?',
        confirmText: 'Delete',
        type: 'is-danger',
        onConfirm: () => this.$emit('delete', this.valueSet.id)
      });
    }
  },
}
</script>

<style scoped>
.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
}

.card-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.card-header:hover {
  background-color: #f9f9f9;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
